import { useQueryClient } from '@tanstack/react-query';
import { delAuth } from '@/api/ApiCalls';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFundContext from '@/contexts/Fund/useFundContext';
import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { NEDARIM_DRAFTS_FEATURE_KEY } from './useSupportRequestDraftsFeature';

/**
 * Support requests filled in somewhere they could not be sent, waiting to be finished here.
 *
 * Drafts are personal: the server returns only the signed-in user's own, in this fund. A
 * colleague's half-written form is not something to find in a shared list.
 */
export const supportRequestDraftsUrl = (fundId?: number, kind?: string) =>
  fundId ? `funds/${fundId}/support-request-drafts${kind ? `?kind=${kind}` : ''}` : '';

/**
 * ⚠️ The kind and the flag travel together and must match. The two drafts are released
 * separately, so listing one kind while asking the other kind's flag would show a list the
 * office has not opened yet.
 */
export function useSupportRequestDrafts(
  kind = 'SupportRequest',
  featureKey: string = NEDARIM_DRAFTS_FEATURE_KEY,
) {
  const { currentFund } = useFundContext();
  const isFeatureOn = useFeature(featureKey);

  /*
   * An empty URL skips the request entirely (the same escape hatch used above for "no fund yet").
   * A flag that is off has to mean no call at all, not a call whose answer is thrown away: at a
   * site where the feature was never released the route may not even be deployed, and a 404 on
   * every load of the support-requests page is noise in the log for nobody's benefit.
   */
  const url = isFeatureOn ? supportRequestDraftsUrl(currentFund?.id, kind) : '';

  const { data, isLoading, isError } = useGetAuthApi<SupportRequestDraftDtoIn[]>(url);

  return {
    drafts: data ?? [],
    isLoading,
    isError,
  };
}

export function useDeleteSupportRequestDraft(kind = 'SupportRequest') {
  const { currentUser } = useAuthContext();
  const { currentFund } = useFundContext();
  const queryClient = useQueryClient();
  const listUrl = supportRequestDraftsUrl(currentFund?.id, kind);

  /**
   * ⚠️ Deliberately not a useDeleteAuthApi hook. That binds the URL when the component
   * renders, and the id being deleted is only known on click - one hook per row would mean a
   * hook count that changes with the list, which React does not allow.
   *
   * Resolves either way: a draft that is already gone is the outcome the caller wanted, and
   * after a submitted request the draft matters far less than the request that replaced it.
   */
  return async (draftId: number) => {
    if (!listUrl) return;

    try {
      await delAuth(`${listUrl.split('?')[0]}/${draftId}`, {
        Authorization: `Bearer ${currentUser?.token}`,
      });
    } finally {
      queryClient.invalidateQueries({ queryKey: [listUrl] });
    }
  };
}
